import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "../components/Layout";
import {
  FaHome,
  FaBook,
  FaUserGraduate,
  FaUsers,
  FaMoneyBill,
  FaShieldAlt,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaHandshake,
  FaBalanceScale,
  FaGraduationCap,
  FaSchool,
  FaFileContract,
  FaEnvelope,
  FaPhone,
  FaChevronRight
} from "react-icons/fa";
import BreadCrumbImage from "../assets/images/breadcrumb.png";

const Terms = () => {
  const termsSections = [
    {
      id: "acceptance",
      title: "Acceptance of Terms",
      icon: FaHandshake,
      content: "By enrolling at our school, parents/guardians and students agree to abide by these Terms & Conditions. These terms govern the relationship between the school, students, and parents/guardians.",
      points: [
        "Effective from date of admission confirmation",
        "Applicable throughout the academic journey",
        "Binding agreement for all parties involved",
        "Subject to periodic updates and revisions"
      ]
    },
    {
      id: "admission",
      title: "Admission & Enrollment",
      icon: FaUserGraduate,
      content: "Admission to our school is subject to specific terms:",
      listItems: [
        "Admission is based on availability of seats and eligibility criteria",
        "Submission of accurate and complete documentation is mandatory",
        "Provision of false information may lead to cancellation of admission",
        "Admission fee is non-refundable once paid",
        "School reserves right to refuse admission without assigning reason"
      ]
    },
    {
      id: "academic",
      title: "Academic Requirements",
      icon: FaBook,
      content: "Students must meet academic standards and requirements:",
      points: [
        "Regular attendance as per school policy",
        "Completion of all assignments and projects",
        "Participation in examinations and assessments",
        "Maintenance of minimum academic performance standards",
        "Compliance with CBSE curriculum guidelines"
      ]
    },
    {
      id: "fees",
      title: "Fee Payment Terms",
      icon: FaMoneyBill,
      content: "Financial obligations and payment terms:",
      listItems: [
        "Fees must be paid by due dates specified in fee schedule",
        "Late payments may attract penalty charges",
        "No reduction in fees for absence or holidays",
        "Fee structure subject to periodic revision with notice",
        "Outstanding fees may result in withholding of results/certificates"
      ]
    },
    {
      id: "conduct",
      title: "Student Conduct & Discipline",
      icon: FaBalanceScale,
      content: "Students are expected to maintain high standards of conduct:",
      points: [
        "Respect for teachers, staff, and fellow students",
        "Adherence to school uniform and appearance guidelines",
        "Prohibition of bullying, harassment, or disruptive behavior",
        "Care for school property and facilities",
        "Compliance with safety and security protocols"
      ]
    },
    {
      id: "attendance",
      title: "Attendance Policy",
      icon: FaCalendarAlt,
      content: "Regular attendance is essential for academic success:",
      listItems: [
        "Minimum 75% attendance required for academic promotion",
        "Leave applications must be submitted in advance",
        "Medical certificates required for extended absence",
        "Unauthorized absence may lead to disciplinary action",
        "Parents must inform school of student absence"
      ]
    },
    {
      id: "parental",
      title: "Parental Responsibilities",
      icon: FaUsers,
      content: "Parents/guardians have specific responsibilities:",
      points: [
        "Regular monitoring of student academic progress",
        "Attendance at parent-teacher meetings",
        "Timely response to school communications",
        "Provision of accurate contact information",
        "Support for school policies and decisions"
      ]
    },
    {
      id: "withdrawal",
      title: "Withdrawal & Transfer",
      icon: FaGraduationCap,
      content: "Procedures for student withdrawal or transfer:",
      listItems: [
        "One month written notice required for withdrawal",
        "Clearance certificate needed for all dues and library books",
        "Transfer certificate issued after all formalities completed",
        "No refund of fees for mid-term withdrawals",
        "School records released only after complete clearance"
      ]
    },
    {
      id: "property",
      title: "School Property & Facilities",
      icon: FaSchool,
      content: "Usage and care of school property:",
      points: [
        "Students responsible for care of school property",
        "Damage to property must be compensated",
        "Proper use of library, laboratory, and sports facilities",
        "Prohibition of unauthorized entry to restricted areas",
        "Technology resources for educational purposes only"
      ]
    },
    {
      id: "liability",
      title: "Limitation of Liability",
      icon: FaShieldAlt,
      content: "School's liability limitations:",
      listItems: [
        "School not liable for loss of personal belongings",
        "Limited liability for accidents during school activities",
        "Not responsible for transportation delays or issues",
        "Medical emergencies handled as per school policy",
        "Force majeure events beyond school's control"
      ]
    },
    {
      id: "amendments",
      title: "Amendments & Updates",
      icon: FaFileContract,
      content: "Terms may be updated periodically:",
      points: [
        "School reserves right to amend terms with notice",
        "Changes communicated through official channels",
        "Continued enrollment implies acceptance of updated terms",
        "Major changes communicated to parents in writing",
        "Current version always available on school website"
      ]
    },
    {
      id: "governing",
      title: "Governing Law & Jurisdiction",
      icon: FaBalanceScale,
      content: "Legal framework for these terms:",
      listItems: [
        "Terms governed by laws of India",
        "Disputes subject to jurisdiction of local courts",
        "CBSE regulations supersede where applicable",
        "Education department guidelines apply",
        "English version prevails for interpretation"
      ]
    }
  ];

  const importantClauses = [
    "These terms supplement the school admission form and prospectus",
    "School rules and regulations are binding on all students",
    "Special circumstances considered on case-by-case basis",
    "School management decisions are final in all matters",
    "Digital communication considered valid and binding"
  ];

  const keyDefinitions = [
    { term: "School", definition: "Refers to [School Name] and its administration" },
    { term: "Parent/Guardian", definition: "Legal guardian responsible for student" },
    { term: "Academic Year", definition: "April to March as per CBSE calendar" },
    { term: "School Hours", definition: "8:30 AM to 2:30 PM unless notified otherwise" },
    { term: "Official Communication", definition: "School notice board, website, or registered email" }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Terms & Conditions | School</title>
        <meta name="description" content="School terms and conditions covering admission, fees, conduct, academic requirements, and parental responsibilities." />
      </Helmet>

      {/* Breadcrumb */}
      <div className="relative bg-gray-900">
        <div className="h-40 md:h-48">
          <img
            src={BreadCrumbImage}
            alt="Breadcrumb"
            className="object-cover w-full h-full opacity-60"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="flex items-center mb-2 text-xs text-white">
            <Link to="/" className="flex items-center transition-colors hover:text-gray-200">
              <FaHome className="w-3 h-3 mr-1" />
              Home
            </Link>
            <span className="mx-1">/</span>
            <span className="font-semibold">Terms & Conditions</span>
          </div>
          <h1 className="text-2xl font-bold text-white md:text-3xl">Terms & Conditions</h1>
          <p className="mt-1 text-xs text-gray-300">Governing terms for school enrollment and operations</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-3 py-6 mx-auto md:px-4 md:py-8">
        
        {/* Header Card */}
        <div className="p-5 mb-8 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <div className="md:w-2/3">
              <div className="flex items-center gap-3 mb-3">
                <FaFileContract className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">Terms & Conditions 2026-27</h2>
              </div>
              <p className="text-gray-700">
                These Terms & Conditions establish the rules and regulations governing the relationship between 
                the school, students, and parents/guardians. They are binding from the date of admission and 
                throughout the academic journey at our institution.
              </p>
            </div>
            <div className="md:w-1/3">
              <div className="p-4 bg-white border border-blue-100 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FaCalendarAlt className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-semibold text-gray-900">Effective Date</span>
                </div>
                <div className="font-medium text-blue-700">April 1, 2026</div>
                <div className="mt-2 text-xs text-gray-600">Academic Year 2026-27</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Definitions */}
        <div className="p-4 mb-8 border border-gray-200 bg-gray-50 rounded-xl">
          <h3 className="mb-3 font-bold text-gray-900">Key Definitions</h3>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
            {keyDefinitions.map((def, index) => (
              <div key={index} className="p-3 bg-white border border-gray-200 rounded-lg">
                <div className="mb-1 text-sm font-semibold text-blue-700">{def.term}</div>
                <div className="text-xs text-gray-600">{def.definition}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Terms Sections */}
        <div className="space-y-6">
          {termsSections.map((section, index) => (
            <div key={section.id} id={section.id} className="scroll-mt-20">
              <div className="overflow-hidden transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-sm">
                {/* Section Header */}
                <div className="p-5 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      index % 4 === 0 ? 'bg-blue-100 text-blue-600' :
                      index % 4 === 1 ? 'bg-emerald-100 text-emerald-600' :
                      index % 4 === 2 ? 'bg-purple-100 text-purple-600' :
                      'bg-amber-100 text-amber-600'
                    }`}>
                      <section.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900">
                        {index + 1}. {section.title}
                      </h3>
                    </div>
                    <div className="text-xs font-medium text-gray-500">
                      Section {index + 1} of {termsSections.length}
                    </div>
                  </div>
                </div>

                {/* Section Content */}
                <div className="p-5">
                  <p className="mb-4 text-gray-700">{section.content}</p>
                  
                  {section.points && (
                    <ul className="space-y-2">
                      {section.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <div className={`w-1.5 h-1.5 mt-2 rounded-full flex-shrink-0 ${
                            index % 4 === 0 ? 'bg-blue-500' :
                            index % 4 === 1 ? 'bg-emerald-500' :
                            index % 4 === 2 ? 'bg-purple-500' :
                            'bg-amber-500'
                          }`}></div>
                          <span className="text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.listItems && (
                    <ul className="space-y-2">
                      {section.listItems.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <FaChevronRight className={`w-3 h-3 mt-1 flex-shrink-0 ${
                            index % 4 === 0 ? 'text-blue-500' :
                            index % 4 === 1 ? 'text-emerald-500' :
                            index % 4 === 2 ? 'text-purple-500' :
                            'text-amber-500'
                          }`} />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Important Clauses */}
        <div className="p-5 mt-8 border bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 rounded-xl">
          <div className="flex items-center gap-3 mb-4">
            <FaExclamationTriangle className="w-6 h-6 text-amber-600" />
            <h3 className="text-lg font-bold text-gray-900">Important Clauses</h3>
          </div>
          
          <div className="grid gap-3">
            {importantClauses.map((clause, index) => (
              <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-white/50">
                <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 rounded-full bg-amber-100">
                  <span className="text-xs font-bold text-amber-700">{index + 1}</span>
                </div>
                <p className="text-sm text-gray-700">{clause}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Acceptance Section */}
        <div className="p-5 mt-8 border border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-white rounded-full">
              <FaHandshake className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="mb-3 text-xl font-bold text-gray-900">Acceptance of Terms</h3>
            <p className="mb-4 text-gray-700">
              By proceeding with admission and enrollment, parents/guardians acknowledge that they have read, 
              understood, and agree to be bound by these Terms & Conditions. Continued enrollment constitutes 
              ongoing acceptance of these terms.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <FaCalendarAlt className="w-4 h-4" />
              <span>These terms are effective for the entire duration of enrollment</span>
            </div>
          </div>
        </div>

        {/* Contact & Queries */}
        <div className="p-5 mt-8 bg-white border border-gray-200 rounded-xl">
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h3 className="mb-3 text-lg font-bold text-gray-900">Questions & Clarifications</h3>
              <p className="mb-4 text-sm text-gray-600">
                For any questions regarding these Terms & Conditions, please contact the school administration.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FaEnvelope className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-700">admin@school.edu</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaPhone className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-700">+91 1234567890</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="mb-3 text-lg font-bold text-gray-900">Office Hours</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>Monday - Friday: 8:00 AM - 4:00 PM</p>
                <p>Saturday: 8:00 AM - 1:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
              <div className="p-3 mt-4 rounded-lg bg-blue-50">
                <p className="text-xs text-blue-700">
                  For urgent matters during non-office hours, please refer to emergency contact numbers provided during admission.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Terms;